import {compact} from '@benchling/js-utils';
import {ScaleLinear} from 'd3';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Sequence} from 'components/Sequence';
import {PolymerLayout, SliceMapType} from 'constants/MapsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Strand} from 'constants/MolbioConstants';
import {SequenceType} from 'constants/SequenceConstants';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  BiointelligentLinkHighlightRegion,
  BORDER_LINE_HEIGHT,
  HoveredLinkIndices,
  LINK_HIGHLIGHT_STROKE_OPACITY,
  LINK_HIGHLIGHT_STROKE_WIDTH,
  LINK_NAME_FONT_SIZE,
  NUCLEOTIDE_RESIDUE_MARGIN,
} from 'molbio/maps/BiointelligentLinksConstants';
import {
  calculateLinkLineLayers,
  getHighlightColor,
  getHighlightOpacity,
  getLinesHeightForSlice,
  getLineYPosAdjustment,
  getRectangularPath,
  getTopLineColor,
  isNonOverlappingPartLinkRegion,
  isNucleotideSequence,
  LinkLineLayers,
} from 'molbio/maps/linear/BiointelligentLinksHelpers';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import LegacyNucleotidePartsView from 'molbio/maps/linear/LegacyNucleotidePartsView';
import {useRedraw} from 'molbio/maps/linear/useRedraw';
import {
  getBiointelligentLinkRegionsForSlice,
  PartHighlightRegion,
} from 'molbio/maps/translateLinksToHighlightRegions';
import Type from 'molbio/maps/Type';
import getTestProps from 'test/lib/cypress/getTestProps';

const AA_SEQUENCE_PART_RESIDUE_MARGIN = 6;
const VALID_NUCLEOTIDE_LINK_TYPES = [
  BiointelligentLinkType.RNA_PART,
  BiointelligentLinkType.DNA_PART,
  BiointelligentLinkType.TRANSLATION,
  BiointelligentLinkType.TRANSCRIPTION,
];
// The minified linear map residue height is a little too short, resulting in the lines covering the
// axis numbers. To fix this, we add to the highlight area so the lines start below the axis numbers.
const MINIMAP_RESIDUE_HEIGHT_ADJUSTMENT = 12;

function validateLinkTypes(polymer: Sequence | AaSequence, linkTypes: Array<BiointelligentLinkType>): void {
  if (isNucleotideSequence(polymer)) {
    assert(polymer.type() !== SequenceType.OLIGO, 'Biontelligent links on oligos are not supported.');
    assert(
      linkTypes.every((linkType) => VALID_NUCLEOTIDE_LINK_TYPES.includes(linkType)),
      `Found invalid link types for nucleotide sequence: ${linkTypes.join(', ')}.`
    );
  } else {
    assert(
      linkTypes.length === 1 && linkTypes[0] === BiointelligentLinkType.AMINO_ACID_PART,
      `Found invalid link types for AA sequence: ${linkTypes.join(', ')}.`
    );
  }
}

function getFontType(): Type {
  return new Type(LINK_NAME_FONT_SIZE);
}

/** Get the height of the line layers for a given link region.
 *
 * E.g. suppose we have 7 links in the slice, and our region covers the last two forward links.
 *
 * ----------------->               |******|
 *   ------>                        ------->
 *             -------->         ----------------->
 * RESIDUESRESIDUES...
 *     <-----------------
 *                   <-------------
 *
 * For forward links, the height is 2 * (line_height + line_gap), because there are two forward links in that region.
 * For reverse links, the height is 0, because there are no reverse links in that region.
 *
 * @param linkRegion The link region to get the height for.
 * @param lineLayers A map of link IDs to their layer index, for either the top or bottom of the slice.
 */
function getLinesHeightForRegion(
  linkRegion: BiointelligentLinkHighlightRegion,
  topLineLayers: LinkLineLayers,
  bottomLineLayers: LinkLineLayers
): {topLinesHeight: number; bottomLinesHeight: number} {
  function getLinesHeightForLink(lineLayers: LinkLineLayers, strand: Strand): number {
    const lineLayerIdxs = compact(linkRegion.links.map((link) => lineLayers[link.id] ?? null));
    if (lineLayerIdxs.length === 0) {
      return 0;
    }
    return getLineYPosAdjustment(Math.max(...lineLayerIdxs), strand);
  }
  return {
    topLinesHeight: getLinesHeightForLink(topLineLayers, 1),
    bottomLinesHeight: getLinesHeightForLink(bottomLineLayers, -1),
  };
}

function calculateBoxHeight(
  polymer: Sequence | AaSequence,
  topLayers: LinkLineLayers,
  bottomLayers: LinkLineLayers,
  forOverlappingParts: boolean
): number {
  const {topLinesHeight} = getLinesHeightForSlice(topLayers, bottomLayers);
  if (isNucleotideSequence(polymer)) {
    const adjustmentForText = forOverlappingParts ? 0 : getFontType().height() + NUCLEOTIDE_RESIDUE_MARGIN;
    const adjustmentForLines = forOverlappingParts ? topLinesHeight : 0;
    return adjustmentForText + adjustmentForLines;
  } else {
    return BORDER_LINE_HEIGHT + AA_SEQUENCE_PART_RESIDUE_MARGIN + topLinesHeight;
  }
}

interface LinkDimensionsParams {
  /** The pixel height of one strand of the residues. */
  residueLayoutHeight: number;
  /** The pixel height of the space between the residues. */
  residueLayoutSpacer: number;
  /** Whether the complement strand is visible. */
  complementVisible: boolean;
  /** The type of the polymer (sequence or protein). */
  polymerType: ModelTypeConstants;
  /** The link region to calculate dimensions for. */
  linkRegion: BiointelligentLinkHighlightRegion;
  /** How to stack each line drawn at the top of the residues, for forward links. */
  topLayers: LinkLineLayers;
  /** How to stack each line drawn at the bottom of the residues, for reverse links. */
  bottomLayers: LinkLineLayers;
  /** Whether we're drawing on the large sequence map or the minified linear map. */
  forMiniMap: boolean;
}

/**
 * Calculates the base height of the highlight region for a polymer.
 * This just accounts for the height needed to highlight the residues, and not additional highlighting to
 * fill space between the lines that border the top or bottom of the highlight.
 */
function getResiduesHeight(
  residueLayoutHeight: number,
  residueLayoutSpacer: number,
  complementVisible: boolean,
  polymerType: ModelTypeConstants
): number {
  if (complementVisible) {
    return 2 * residueLayoutHeight + residueLayoutSpacer;
  } else if (polymerType === ModelTypeConstants.PROTEIN) {
    return residueLayoutHeight + AA_SEQUENCE_PART_RESIDUE_MARGIN;
  } else {
    // No spacer in the middle if we only have one strand.
    return residueLayoutHeight;
  }
}

/** Gets the y-position (relative to the full box height) of where the residues start. */
function getYPositionOfTopOfResidues(params: LinkDimensionsParams): number {
  const {topLayers, bottomLayers, residueLayoutSpacer} = params;
  const {topLinesHeight} = getLinesHeightForSlice(topLayers, bottomLayers);
  if (params.polymerType === ModelTypeConstants.PROTEIN) {
    return topLinesHeight + residueLayoutSpacer - AA_SEQUENCE_PART_RESIDUE_MARGIN;
  }
  return topLinesHeight + residueLayoutSpacer;
}

/**
 * Calculates the y-position of the colored line above/below the top of the highlight region for each link.
 *
 * - The top of the residues is our starting point.
 * - From there, we figure out how much to subtract to move the line up to its layer.
 * - Each line stacked bumps us up by the line's height plus the gap between it and the other lines.
 */
function calculateLinePosition(
  params: LinkDimensionsParams & {highlightedStrand: Strand; layerIndex: number}
): number {
  const {
    highlightedStrand,
    layerIndex,
    residueLayoutHeight,
    residueLayoutSpacer,
    complementVisible,
    forMiniMap,
    polymerType,
  } = params;
  const residuesHeight =
    getResiduesHeight(residueLayoutHeight, residueLayoutSpacer, complementVisible, polymerType) +
    (forMiniMap ? MINIMAP_RESIDUE_HEIGHT_ADJUSTMENT : 0);
  const topOfResidues = getYPositionOfTopOfResidues(params);
  if (highlightedStrand === -1) {
    return topOfResidues + residuesHeight + getLineYPosAdjustment(layerIndex, -1);
  }
  return topOfResidues - getLineYPosAdjustment(layerIndex, 1);
}

/**
 * Calculates the starting y-position of the background of the highlight region for a biointelligent link.
 *
 * A higher y-value means that the highlight starts *lower* on the map.
 */
function getBackgroundY(params: LinkDimensionsParams & {highlightedStrand: Strand}): number {
  const {residueLayoutHeight, residueLayoutSpacer, highlightedStrand, topLayers, bottomLayers, linkRegion} =
    params;
  const topOfResidues = getYPositionOfTopOfResidues(params);
  const {topLinesHeight} = getLinesHeightForRegion(linkRegion, topLayers, bottomLayers);
  if (highlightedStrand === 1 || highlightedStrand === 0) {
    return topOfResidues - topLinesHeight;
  }
  return topOfResidues + residueLayoutHeight + residueLayoutSpacer / 2;
}

export function calculateLinkDimensions(params: LinkDimensionsParams): {
  forwardParams: {
    backgroundY: number;
    height: number;
  } | null;
  reverseParams: {
    backgroundY: number;
    height: number;
  } | null;
} {
  const {
    residueLayoutHeight,
    residueLayoutSpacer,
    complementVisible,
    polymerType,
    topLayers,
    bottomLayers,
    linkRegion,
    forMiniMap,
  } = params;
  const {highlightedStrand} = linkRegion;
  const residuesHeight =
    getResiduesHeight(residueLayoutHeight, residueLayoutSpacer, complementVisible, polymerType) +
    (forMiniMap ? MINIMAP_RESIDUE_HEIGHT_ADJUSTMENT : 0);
  const {topLinesHeight, bottomLinesHeight} = getLinesHeightForRegion(linkRegion, topLayers, bottomLayers);
  const forwardBackgroundY = getBackgroundY({...params, highlightedStrand: 1});
  const reverseBackgroundY = getBackgroundY({...params, highlightedStrand: -1});
  if (!complementVisible) {
    // When the complement is not visible, we always highlight the forward strand.
    return {
      forwardParams: {
        height: residuesHeight + topLinesHeight + bottomLinesHeight,
        backgroundY: forwardBackgroundY,
      },
      reverseParams: null,
    };
  }

  const highlightForward = highlightedStrand === 1 || highlightedStrand === 0;
  const highlightReverse = highlightedStrand === -1 || highlightedStrand === 0;
  return {
    forwardParams: highlightForward
      ? {
          backgroundY: forwardBackgroundY,
          height: residuesHeight / 2 + topLinesHeight,
        }
      : null,
    reverseParams: highlightReverse
      ? {
          backgroundY: reverseBackgroundY,
          height: residuesHeight / 2 + bottomLinesHeight,
        }
      : null,
  };
}

export interface BiointelligentLinkHighlightViewProps {
  /** A function that takes the index of a residue and returns its x-axis position in pixels on the slice */
  xFn: ScaleLinear<number, number>;
  /** The residue index at which the slice starts */
  sliceStart: number;
  /** The residue index at which the slice ends */
  sliceEnd: number;
  /** The Backbone sequence or protein being rendered */
  polymer: Sequence | AaSequence;
  /** Flat object layout data */
  polymerLayout: PolymerLayout;
  /** Height in pixels of the residues at default zoom. */
  residueLayoutHeight: number;
  /** Height in pixels of the space below the residues at default zoom. */
  residueLayoutSpacer: number;
  /** Whether or not the complement slice is visible */
  complementVisible: boolean;
  /** What kind of map we're drawing on */
  mapType: SliceMapType;
  /**
   * A function that accepts a new box height or a way to calculate it and triggers a redraw with the new box
   * height.
   * */
  redraw: (newBoxHeight?: number | ((layout: PolymerLayout) => number)) => void;
  /** The types of links being rendered */
  linkTypes: Array<BiointelligentLinkType>;
  /** The coordinates of the link the user is currently hovering over, if any */
  hoveredLinkIndices: HoveredLinkIndices | null;
}

/**
 * Responsible for rendering the gray highlight over the residues for a biointelligent link, plus the
 * thin colored lines (not annotations) that border the top or bottom of the highlight.
 *
 * The overall rendering strategy is to draw the gray highlight first, covering both the bases and any
 * additional height from the stacked lines. Then, we draw the colored lines on top of the gray highlight.
 */
export default function BiointelligentLinkHighlightView(
  props: BiointelligentLinkHighlightViewProps
): JSX.Element {
  validateLinkTypes(props.polymer, props.linkTypes);

  const linkRegions = getBiointelligentLinkRegionsForSlice(
    props.sliceStart,
    props.sliceEnd,
    props.polymer,
    props.linkTypes
  );
  const {topLayers, bottomLayers} = calculateLinkLineLayers(linkRegions, props.polymer.length());

  useRedraw({
    redraw: props.redraw,
    shouldRedraw: () => linkRegions.length > 0,
    newBoxHeight: calculateBoxHeight(
      props.polymer,
      topLayers,
      bottomLayers,
      BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS
    ),
  });

  const partialLinkParams: Omit<LinkDimensionsParams, 'linkRegion'> = {
    residueLayoutHeight: props.residueLayoutHeight,
    residueLayoutSpacer: props.residueLayoutSpacer,
    complementVisible: props.complementVisible,
    polymerType: isNucleotideSequence(props.polymer) ? props.polymer.subtype() : ModelTypeConstants.PROTEIN,
    topLayers,
    bottomLayers,
    forMiniMap: props.mapType === SliceMapType.minimap,
  };

  if (isNucleotideSequence(props.polymer) && !BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
    assert(
      props.linkTypes.length === 1 &&
        [BiointelligentLinkType.RNA_PART, BiointelligentLinkType.DNA_PART].includes(props.linkTypes[0]),
      'New-style parts must be enabled to higlight any link types other than parts in this view.'
    );
    const fontType = getFontType();
    const backgroundY = fontType.height() + NUCLEOTIDE_RESIDUE_MARGIN;
    const height = getResiduesHeight(
      props.residueLayoutHeight,
      props.residueLayoutSpacer,
      props.complementVisible,
      partialLinkParams.polymerType
    );
    return (
      <LegacyNucleotidePartsView
        polymerLayout={props.polymerLayout}
        partRegions={linkRegions as Array<PartHighlightRegion>}
        xFn={props.xFn}
        sliceStart={props.sliceStart}
        sliceEnd={props.sliceEnd}
        backgroundY={backgroundY}
        height={height}
        sequenceLength={props.polymer.length()}
        type={fontType}
        mapType={props.mapType}
      />
    );
  }

  return (
    <ComponentWrapper
      layout={props.polymerLayout}
      shouldStopMouseDownPropagation={false}
      dataTestAttrs={{
        component: 'BiointelligentLinkHighlightView',
        element: 'root',
      }}
    >
      <>
        {linkRegions.map((linkRegion: BiointelligentLinkHighlightRegion, index: number) => {
          const {forwardParams, reverseParams} = calculateLinkDimensions({
            ...partialLinkParams,
            linkRegion,
          });
          return (
            <g
              key={`${props.sliceStart}-${props.sliceEnd}-${index}`}
              {...getTestProps({
                component: 'BiointelligentLinkHighlightView',
                element: 'highlight-region',
                index: index.toString(),
              })}
            >
              {forwardParams && (
                <path
                  key={`${props.sliceStart}-${props.sliceEnd}-${index}-forward`}
                  {...getTestProps({
                    component: 'BiointelligentLinkHighlightView',
                    element: 'highlight-path',
                    key: 'forward',
                  })}
                  d={getRectangularPath(
                    linkRegion,
                    props.sliceStart,
                    props.sliceEnd,
                    props.xFn,
                    forwardParams.backgroundY,
                    forwardParams.height,
                    props.polymer.length()
                  )}
                  stroke={getHighlightColor({
                    region: linkRegion,
                    highlightedStrand: 1,
                    hoveredLinkIndices: props.hoveredLinkIndices,
                    complementVisible: props.complementVisible,
                  })}
                  strokeWidth={LINK_HIGHLIGHT_STROKE_WIDTH}
                  strokeOpacity={LINK_HIGHLIGHT_STROKE_OPACITY}
                  fill={getHighlightColor({
                    region: linkRegion,
                    highlightedStrand: 1,
                    hoveredLinkIndices: props.hoveredLinkIndices,
                    complementVisible: props.complementVisible,
                  })}
                  fillOpacity={getHighlightOpacity(linkRegion, true)}
                  data-amp-safe={false}
                />
              )}
              {reverseParams && (
                <path
                  key={`${props.sliceStart}-${props.sliceEnd}-${index}-reverse`}
                  {...getTestProps({
                    component: 'BiointelligentLinkHighlightView',
                    element: 'highlight-path',
                    key: 'reverse',
                  })}
                  d={getRectangularPath(
                    linkRegion,
                    props.sliceStart,
                    props.sliceEnd,
                    props.xFn,
                    reverseParams.backgroundY,
                    reverseParams.height,
                    props.polymer.length()
                  )}
                  stroke={getHighlightColor({
                    region: linkRegion,
                    highlightedStrand: -1,
                    hoveredLinkIndices: props.hoveredLinkIndices,
                    complementVisible: props.complementVisible,
                  })}
                  strokeWidth={LINK_HIGHLIGHT_STROKE_WIDTH}
                  strokeOpacity={LINK_HIGHLIGHT_STROKE_OPACITY}
                  fill={getHighlightColor({
                    region: linkRegion,
                    highlightedStrand: -1,
                    hoveredLinkIndices: props.hoveredLinkIndices,
                    complementVisible: props.complementVisible,
                  })}
                  fillOpacity={getHighlightOpacity(linkRegion, true)}
                  data-amp-safe={false}
                />
              )}
              {linkRegion.links.map((link, j) => (
                <path
                  key={`${props.sliceStart}-${props.sliceEnd}-${index}-${j}-line`}
                  {...getTestProps({
                    component: 'BiointelligentLinkHighlightView',
                    element: 'highlight-path',
                    key: 'line',
                  })}
                  d={getRectangularPath(
                    linkRegion,
                    props.sliceStart,
                    props.sliceEnd,
                    props.xFn,
                    calculateLinePosition({
                      ...partialLinkParams,
                      highlightedStrand: link.highlightedStrand ?? 0,
                      linkRegion,
                      layerIndex: topLayers[link.id] ?? bottomLayers[link.id] ?? 0,
                    }),
                    BORDER_LINE_HEIGHT,
                    props.polymer.length()
                  )}
                  fill={getTopLineColor(
                    link.linkType,
                    isNonOverlappingPartLinkRegion(linkRegion) && linkRegion.outOfSync
                  )}
                  fillOpacity={getHighlightOpacity(linkRegion, true)}
                  data-amp-safe={false}
                />
              ))}
            </g>
          );
        })}
      </>
    </ComponentWrapper>
  );
}
